// Dashboard widget detail modal — opens when clicking any widget.
// Shows a clear, structured preview of what the widget represents and offers
// concrete follow-up actions instead of jumping straight to another page.
import { createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ArrowUpRight } from "lucide-react";
import {
  Users, CalendarDays, AlertCircle, ClipboardList, TrendingUp, Clock,
  MapPin, Building2, Timer, User as UserIcon, UsersRound, Bell, Megaphone,
  Cake, PlaneTakeoff, HandCoins, Trophy, Zap, LayoutGrid, BarChart3,
  Siren, CheckCircle2, TelescopeIcon, PlaneLanding, Send, Plus, Settings2,
} from "lucide-react";

// Context so any widget (StatCard, WidgetShell) can trigger the modal
// without threading props everywhere.
export const DashboardCtx = createContext({ openDetail: () => {} });
export const useDashboardCtx = () => useContext(DashboardCtx);

// Per-widget action lists — each item becomes a button in the modal.
// Keep 1-3 actions per widget to stay focused and legible.
const ACTIONS = {
  // ---- STATS ----
  stat_employees: [
    { to: "/employees", icon: Users, label: "Bekijk medewerkers", desc: "Volledige team-directory openen" },
    { to: "/employees", icon: Plus, label: "Nieuwe medewerker uitnodigen", desc: "Genereer een 6-letter code" },
  ],
  stat_shifts_week: [
    { to: "/roster", icon: CalendarDays, label: "Open rooster", desc: "Bekijk en bewerk deze week" },
    { to: "/roster", icon: Plus, label: "Dienst toevoegen", desc: "Plan een nieuwe dienst" },
  ],
  stat_open_shifts: [
    { to: "/roster", icon: AlertCircle, label: "Open diensten in rooster", desc: "Ga naar rooster om toe te wijzen" },
    { to: "/quick-assign", icon: Zap, label: "Snel toewijzen (mobiel)", desc: "Wijs open diensten met één tik toe" },
  ],
  stat_pending_leave: [
    { to: "/requests", icon: PlaneTakeoff, label: "Verlofaanvragen beoordelen", desc: "Goedkeuren of afwijzen" },
  ],
  stat_pending_swaps: [
    { to: "/swaps", icon: TrendingUp, label: "Ruilverzoeken bekijken", desc: "Beslis over pending wissels" },
  ],
  stat_pending_time: [
    { to: "/timesheets", icon: Clock, label: "Uren controleren", desc: "Beoordeel ingediende uren" },
  ],
  stat_locations: [
    { to: "/locations", icon: MapPin, label: "Locaties beheren", desc: "Vestigingen, kleuren en adressen" },
  ],
  stat_teams: [
    { to: "/teams", icon: Building2, label: "Teams beheren", desc: "Groepen medewerkers organiseren" },
  ],
  stat_total_hours: [
    { to: "/roster", icon: CalendarDays, label: "Open rooster", desc: "Bekijk uren-verdeling per dag" },
    { to: "/reports", icon: BarChart3, label: "Uren-rapport", desc: "Exporteer als CSV" },
  ],
  roster_horizon: [
    { to: "/roster", icon: TelescopeIcon, label: "Open rooster", desc: "Plan verder vooruit" },
  ],

  // ---- LISTS ----
  my_shifts: [
    { to: "/my-roster", icon: UserIcon, label: "Mijn rooster", desc: "Volledige weekweergave" },
  ],
  team_today: [
    { to: "/team-today", icon: UsersRound, label: "Team vandaag", desc: "Wie werkt er nu en wanneer" },
  ],
  recent_activity: [
    { to: "/notifications", icon: Bell, label: "Alle meldingen", desc: "Bekijk het volledige overzicht" },
  ],
  recent_news: [
    { to: "/news", icon: Megaphone, label: "Naar Nieuws", desc: "Alle recente berichten" },
  ],
  upcoming_birthdays: [
    { to: "/news", icon: Cake, label: "Verjaardagen bekijken", desc: "Feliciteer je collega's" },
  ],
  pending_leave_list: [
    { to: "/requests", icon: PlaneTakeoff, label: "Aanvragen beoordelen", desc: "Goedkeuren of afwijzen" },
  ],
  open_shifts_list: [
    { to: "/roster", icon: HandCoins, label: "Open diensten in rooster", desc: "Wijs medewerkers toe" },
  ],
  top_hours_employees: [
    { to: "/reports", icon: Trophy, label: "Uren-rapport", desc: "Ranglijst met details" },
  ],
  urgent_open_shifts: [
    { to: "/quick-assign", icon: Siren, label: "Snel toewijzen", desc: "Los urgente open diensten op" },
    { to: "/roster", icon: CalendarDays, label: "Open rooster", desc: "Bekijk in context" },
  ],
  publish_status: [
    { to: "/roster", icon: CheckCircle2, label: "Publiceren", desc: "Publiceer de rest van je week" },
  ],
  leave_taken_ytd: [
    { to: "/requests", icon: PlaneLanding, label: "Verlofoverzicht", desc: "Bekijk alle aanvragen dit jaar" },
    { to: "/reports", icon: BarChart3, label: "Verlof-rapport", desc: "Exporteer als CSV" },
  ],
  broadcast_composer: [
    { to: "/notifications", icon: Send, label: "Naar Meldingen", desc: "Broadcast uitgebreid opstellen" },
  ],

  // ---- WIDE ----
  quick_actions: [
    { to: "/roster", icon: CalendarDays, label: "Rooster", desc: "Plannen deze week" },
    { to: "/employees", icon: Users, label: "Medewerkers", desc: "Team beheren" },
  ],
  week_calendar: [
    { to: "/roster", icon: LayoutGrid, label: "Volledige weekweergave", desc: "Zoom in op één dag" },
  ],
  hours_bar: [
    { to: "/roster", icon: BarChart3, label: "Open rooster", desc: "Bekijk gedetailleerd" },
    { to: "/reports", icon: BarChart3, label: "Uren-rapport", desc: "Exporteer als CSV" },
  ],
};

// Small helper: display a "current value" preview inside the modal.
function currentValue(widgetId, data, t) {
  const s = data?.stats || {};
  switch (widgetId) {
    case "stat_employees":     return s.employees;
    case "stat_shifts_week":   return s.shifts_this_week;
    case "stat_open_shifts":   return s.open_shifts;
    case "stat_pending_leave": return s.pending_leave;
    case "stat_pending_swaps": return s.pending_swaps;
    case "stat_pending_time":  return s.pending_time;
    case "stat_locations":     return s.locations;
    case "stat_teams":         return s.teams;
    case "my_shifts":          return `${(data?.myShifts || []).length} ${t("shifts_short")}`;
    case "team_today":         return `${(data?.todayShifts || []).filter((x) => x.employee_id).length}`;
    case "pending_leave_list": return `${(data?.leaves || []).filter((l) => l.status === "pending").length}`;
    case "open_shifts_list":   return `${(data?.weekShifts || []).filter((s) => !s.employee_id).length}`;
    case "recent_activity":    return `${(data?.notifications || []).length}`;
    case "recent_news":        return `${(data?.news || []).length}`;
    default: return null;
  }
}

export default function WidgetDetailModal({ widget, data, onClose, t }) {
  const actions = widget ? (ACTIONS[widget.id] || []) : [];
  const value = widget ? currentValue(widget.id, data, t) : null;
  const Icon = widget?.icon || Settings2;
  return (
    <AnimatePresence>
      {widget && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md grid place-items-center p-4"
          onClick={onClose}
          data-testid="widget-detail-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-md p-0 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            {/* Header */}
            <div className="p-6 border-b hairline flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-2xl grid place-items-center shrink-0"
                style={{ background: "hsl(var(--primary-soft))", color: "hsl(var(--primary))" }}
              >
                <Icon size={20} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="eyebrow">{t("widget_detail_eyebrow")}</div>
                <h3 className="font-display text-[22px] font-bold tracking-tight text-[hsl(var(--text-strong))] mt-0.5 leading-tight">
                  {t(widget.titleKey)}
                </h3>
                <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">
                  {t(widget.descKey)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] transition-colors shrink-0"
                data-testid="widget-detail-close"
                aria-label={t("close")}
              >
                <X size={16} />
              </button>
            </div>

            {/* Current value snapshot */}
            {value != null && value !== "" && (
              <div className="px-6 py-4 border-b hairline bg-[hsl(var(--muted))/0.4]">
                <div className="eyebrow mb-1">{t("widget_detail_current")}</div>
                <div className="font-display text-[36px] leading-none tabular-nums font-bold text-[hsl(var(--text-strong))]">
                  {value}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="p-3 flex flex-col gap-2">
              <div className="eyebrow px-3 pt-1">{t("widget_detail_actions")}</div>
              {actions.length === 0 && (
                <div className="px-3 py-4 text-[12.5px] text-[hsl(var(--text-muted))]">
                  {t("widget_detail_no_actions")}
                </div>
              )}
              {actions.map((a, i) => {
                const AIcon = a.icon || ArrowUpRight;
                return (
                  <Link
                    key={i}
                    to={a.to}
                    onClick={onClose}
                    data-testid={`widget-detail-action-${i}`}
                    className="group flex items-center gap-3 rounded-2xl border hairline p-3.5 bg-white hover:bg-[hsl(var(--primary-soft))] hover:border-[hsl(var(--primary))] transition-colors"
                  >
                    <div className="w-9 h-9 rounded-xl bg-[hsl(var(--muted))] text-[hsl(var(--text-strong))] group-hover:bg-[hsl(var(--primary))] group-hover:text-white grid place-items-center shrink-0 transition-colors">
                      <AIcon size={15} strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold text-[hsl(var(--text-strong))] leading-tight">{a.label}</div>
                      {a.desc && (
                        <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">{a.desc}</div>
                      )}
                    </div>
                    <ArrowUpRight size={14} className="text-[hsl(var(--text-muted))] group-hover:text-[hsl(var(--primary))] shrink-0" />
                  </Link>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
